import { useContext, useState, useEffect } from "react";
import { ServicesContext, IServicesContext } from "../../context/servicesContext";
import { groupServicesInfo } from "../helpers/groupServicesInfo";
import { countriesService } from "../../api/countriesService/countriesService";
import { computeCountries } from "../helpers/computeCountries";
import { CountriesSelectOption } from "../../types/countries";
import { servicesService } from "../../api/servicesService/servicesService";
import { ServicesInfoComputedType } from "../../types/servicesInfo";
import { useLocation } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";

export const useSidebarDataLoader = () => {
  // Leggi i dati dal context globale dei servizi
  const { services } = useContext(ServicesContext) as IServicesContext;
  const location = useLocation();

  // State per i paesi
  const [countries, setCountries] = useState<CountriesSelectOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);

  // State per i dati dei servizi info
  const [servicesInfo, setServicesInfo] = useState<ServicesInfoComputedType | null>(null);
  const [servicesInfoLoading, setServicesInfoLoading] = useState(false);
  const [servicesInfoError, setServicesInfoError] = useState<string | null>(null);

  // Carica i dati dei servizi info
  useEffect(() => {
    const loadServicesInfo = async () => {
      if (!servicesInfo) {
        setServicesInfoLoading(true);
        setServicesInfoError(null);
        
        try {
          console.log('useSidebarDataLoader: Fetching servicesInfo data');
          const response = await servicesService.serviceInfo();
          if (response.success) {
            console.log('ServicesInfo loaded successfully:', response.responseBody);
            
            // Controlla se i dati sono già raggruppati (hanno la struttura ServicesInfoComputedType)
            const responseData = response.responseBody;
            
            console.log('ServicesInfo response structure:', {
              hasMainServices: !!responseData.mainServices,
              mainServicesLength: responseData.mainServices?.length,
              firstMainService: responseData.mainServices?.[0],
              hasAdditionalServices: !!responseData.additionalServices,
              additionalServicesLength: responseData.additionalServices?.length,
              firstAdditionalService: responseData.additionalServices?.[0]
            });
            
            let processedData;
            
            // Se i dati hanno già la struttura raggruppata (con mainServices e additionalServices come array di categorie)
            if (responseData.mainServices && Array.isArray(responseData.mainServices) && 
                responseData.mainServices.length > 0 && 
                responseData.mainServices[0].servicesInfo) {
              // I dati sono già raggruppati
              console.log('ServicesInfo data is already grouped');
              processedData = responseData;
            } else {
              // I dati sono piatti, dobbiamo raggrupparli
              console.log('ServicesInfo data is flat, grouping...');
              processedData = groupServicesInfo({
                mainServices: responseData.mainServices || [],
                additionalServices: responseData.additionalServices || []
              });
              console.log('Grouped ServicesInfo data:', processedData);
            }
            
            setServicesInfo(processedData);
          } else {
            setServicesInfoError('Errore nel caricamento dei servizi info');
            console.error('ServicesInfo API error:', response);
          }
        } catch (error) {
          setServicesInfoError('Errore nel caricamento dei servizi info');
          console.error('ServicesInfo API error:', error);
        } finally {
          setServicesInfoLoading(false);
        }
      }
    };

    loadServicesInfo();
  }, [servicesInfo]);

  // Carica i dati dei paesi
  useEffect(() => {
    // Se siamo nella dashboard, salta il caricamento di countries
    if (location.pathname === routeUrls.dashboard) {
      console.log('Dashboard detected - skipping countries in useSidebarDataLoader');
      return;
    }

    const loadCountries = async () => {
      if (countries.length === 0) {
        setCountriesLoading(true);
        setCountriesError(null);
        
        try {
          const response = await countriesService.countries();
          if (response.success) {
            const computedCountries = computeCountries(response.responseBody);
            setCountries(computedCountries);
            console.log('Countries loaded successfully:', computedCountries);
          } else {
            setCountriesError('Errore nel caricamento dei paesi');
            console.error('Countries API error:', response);
          }
        } catch (error) {
          setCountriesError('Errore nel caricamento dei paesi');
          console.error('Countries API error:', error);
        } finally {
          setCountriesLoading(false);
        }
      }
    };

    loadCountries();
  }, [countries.length, location.pathname]);

  return {
    countries,
    servicesInfo,
    isLoading: countriesLoading || servicesInfoLoading,
    error: countriesError || servicesInfoError,
    reloadData: () => {
      setCountries([]); // Reset per forzare il ricaricamento
      setServicesInfo(null); // Reset per forzare il ricaricamento dei servizi info
    },
  };
}; 